/**
 * QgsAuditSummaryPage
 *
 * QGS audit summary — replaces the QAS AuditSummaryPage for any form
 * whose gate_type belongs to the QGS set (QG1 / QG_FAN / QG2 / B_KANSA).
 *
 * There is no scoring in QGS, so this page intentionally does NOT render:
 *   - deduction points / points tiles
 *   - Category Deductions card
 *   - Fixed Before Shipment checkboxes
 *   - Group A / Group B validation
 *   - Total Score
 *
 * What it shows instead:
 *   - Audit metadata (session id, form, gate, unit serial, started at)
 *   - Backend-truth KO / Rework counter pills (from qgsSubmissionStore's
 *     latest submission_summary — updated on every batched flush)
 *   - Open KOs — every KO synced during the audit, with defect type /
 *     location, initial comment, photo thumbnail, and current status.
 *   - Rework records — every Rework synced during the audit, with defect
 *     type / location / rework type, comment, and photo thumbnail.
 *
 * Data source: qgsSubmissionStore holds a snapshot of every response the
 * operator picked in-session (populated by ChecklistPage's write-through
 * mirror). Master lookup labels are fetched here once on mount.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Send, Loader2, Home, AlertOctagon, Wrench, Info } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useTranslation } from '../hooks/useTranslation';
import * as qgsStore from '../qgsSubmissionStore';


const API = (process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000') + '/api/frontend/qgs';


function useLookups() {
  const [lookups, setLookups] = useState({
    defect_types: [], defect_locations: [], rework_types: [],
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const parse = async (path) => {
        try {
          const r = await fetch(`${API}${path}?page_size=500`, { credentials: 'include' });
          if (!r.ok) return [];
          const b = await r.json();
          return b.results || b || [];
        } catch { return []; }
      };
      const [dt, dl, rt] = await Promise.all([
        parse('/defect-types/'),
        parse('/defect-locations/'),
        parse('/rework-types/'),
      ]);
      if (!cancelled) setLookups({ defect_types: dt, defect_locations: dl, rework_types: rt });
    })();
    return () => { cancelled = true; };
  }, []);
  return lookups;
}


function labelFor(list, id, language) {
  if (!id) return '';
  const row = list.find(r => String(r.id) === String(id));
  if (!row) return `#${id}`;
  return (language === 'it' ? row.label_it : row.label_en) || row.label_en || row.code || `#${id}`;
}


function MetaRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0">
      <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
      <span className="text-sm text-slate-800 font-medium text-right">{value || '—'}</span>
    </div>
  );
}


function CountPill({ tone, count, label }) {
  const palette = tone === 'ko'
    ? 'bg-red-50 border-red-200 text-red-700'
    : 'bg-orange-50 border-orange-200 text-orange-700';
  return (
    <div className={`flex-1 border rounded-xl p-4 ${palette}`}>
      <div className="text-3xl font-bold leading-none">{count}</div>
      <div className="text-xs uppercase tracking-wider mt-2 opacity-80 font-semibold">{label}</div>
    </div>
  );
}


function KoCard({ record, lookups, language, t }) {
  const d = record.details || {};
  return (
    <Card className="mb-3 border-l-4 border-l-red-500">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900 truncate">{record.checkLabel || `Check #${record.checkId}`}</div>
            <div className="text-xs text-slate-500 mt-1">
              {labelFor(lookups.defect_types, d.defect_type_id, language)}
              {' · '}
              {labelFor(lookups.defect_locations, d.defect_location_id, language)}
            </div>
          </div>
          <Badge variant="destructive" className="shrink-0">KO</Badge>
        </div>
        {(record.comment || d.note) && (
          <div className="text-xs text-slate-700 bg-slate-50 rounded p-2 mt-2 whitespace-pre-wrap">
            {record.comment || d.note}
          </div>
        )}
        {record.photoUrl && (
          <a href={record.photoUrl} target="_blank" rel="noreferrer" className="inline-block mt-2">
            <img
              src={record.photoUrl}
              alt="KO evidence"
              className="w-24 h-24 object-cover rounded border border-slate-200"
            />
          </a>
        )}
        <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-2">
          {record.synced ? t('summary.qgs_synced') : t('summary.qgs_pending_sync')}
        </div>
      </CardContent>
    </Card>
  );
}


function ReworkCard({ record, lookups, language, t }) {
  const d = record.details || {};
  return (
    <Card className="mb-3 border-l-4 border-l-orange-500">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900 truncate">{record.checkLabel || `Check #${record.checkId}`}</div>
            <div className="text-xs text-slate-500 mt-1">
              {labelFor(lookups.defect_types, d.defect_type_id, language)}
              {' · '}
              {labelFor(lookups.defect_locations, d.defect_location_id, language)}
              {' · '}
              <span className="font-semibold">{labelFor(lookups.rework_types, d.rework_type_id, language)}</span>
            </div>
          </div>
          <Badge className="shrink-0 bg-orange-500 hover:bg-orange-600">REWORK</Badge>
        </div>
        {(record.comment || d.note) && (
          <div className="text-xs text-slate-700 bg-slate-50 rounded p-2 mt-2 whitespace-pre-wrap">
            {record.comment || d.note}
          </div>
        )}
        {record.photoUrl && (
          <a href={record.photoUrl} target="_blank" rel="noreferrer" className="inline-block mt-2">
            <img
              src={record.photoUrl}
              alt="Rework evidence"
              className="w-24 h-24 object-cover rounded border border-slate-200"
            />
          </a>
        )}
        <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-2">
          {record.synced ? t('summary.qgs_synced') : t('summary.qgs_pending_sync')}
        </div>
      </CardContent>
    </Card>
  );
}


const QgsAuditSummaryPage = ({
  currentAuditResponse,
  selectedForm,
  onNavigateBack,
  onNavigateToDashboard,
  onFinalSubmit,
  isSubmitting = false,
}) => {
  const { t, language } = useTranslation();
  const lookups = useLookups();

  // Subscribe to the store so counts + records refresh live if the user
  // navigates back to the checklist and picks more responses.
  const [tick, setTick] = useState(0);
  useEffect(() => qgsStore.subscribe(() => setTick(x => x + 1)), []);

  const submissionSummary = qgsStore.getSubmissionSummary();
  const records = useMemo(() => qgsStore.allRecords(), [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const koRows = useMemo(
    () => Object.entries(records).filter(([, r]) => r.response === 'KO').map(([checkId, r]) => ({ checkId, ...r })),
    [records],
  );
  const reworkRows = useMemo(
    () => Object.entries(records).filter(([, r]) => r.response === 'REWORK').map(([checkId, r]) => ({ checkId, ...r })),
    [records],
  );

  const koCount = submissionSummary ? submissionSummary.knockouts_count : koRows.length;
  const reworkCount = submissionSummary ? submissionSummary.reworks_count : reworkRows.length;

  const serialNumber = currentAuditResponse?.raw_response_json?.serialNumber
    || currentAuditResponse?.raw_response_json?.unit_serial_number
    || '';
  const sessionId = submissionSummary?.session_id
    || currentAuditResponse?.audit_form_response_id
    || '';
  const startedAt = currentAuditResponse?.created_at
    || currentAuditResponse?.raw_response_json?.startedAt
    || '';

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">{t('summary.title')}</h1>
          <p className="text-xs md:text-sm text-slate-500">{t('summary.qgs_subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onNavigateToDashboard}>
            <Home className="w-4 h-4 mr-1" /> {t('summary.dashboard')}
          </Button>
          <Button variant="outline" size="sm" onClick={onNavigateBack}>
            <ArrowLeft className="w-4 h-4 mr-1" /> {t('summary.back_to_audit')}
          </Button>
          <Button
            onClick={onFinalSubmit}
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
          >
            {isSubmitting
              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {t('summary.submitting')}</>
              : <><Send className="w-4 h-4 mr-1" /> {t('summary.submit_audit')}</>}
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-4 md:p-8 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">

        {/* Left rail — metadata + counts */}
        <aside className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Info className="w-4 h-4 text-slate-500" />
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">{t('summary.qgs_audit_info')}</h2>
              </div>
              <MetaRow label={t('summary.qgs_session')} value={sessionId} />
              <MetaRow label={t('summary.qgs_form')} value={selectedForm?.title || selectedForm?.form_name || selectedForm?.code || ''} />
              <MetaRow label={t('summary.qgs_gate')} value={selectedForm?.gate_type} />
              <MetaRow label={t('summary.qgs_serial')} value={serialNumber} />
              <MetaRow label={t('summary.qgs_started')} value={startedAt ? String(startedAt).slice(0, 16).replace('T', ' ') : ''} />
              <MetaRow label={t('summary.qgs_status')} value={submissionSummary?.status || 'DRAFT'} />
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <CountPill tone="ko" count={koCount} label={t('summary.qgs_open_ko')} />
            <CountPill tone="rework" count={reworkCount} label={t('summary.qgs_rework')} />
          </div>

          {!submissionSummary && (
            <div className="text-xs text-slate-500 bg-slate-100 rounded p-3">
              {t('summary.qgs_no_sync_hint')}
            </div>
          )}
        </aside>

        {/* Right — detailed lists */}
        <section className="min-w-0">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <AlertOctagon className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-bold text-slate-900">{t('summary.qgs_open_ko_heading', { count: koRows.length })}</h2>
            </div>
            {koRows.length === 0 ? (
              <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg p-6 text-center bg-white">
                {t('summary.qgs_no_kos')}
              </div>
            ) : (
              koRows.map(r => <KoCard key={`ko-${r.checkId}`} record={r} lookups={lookups} language={language} t={t} />)
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="w-5 h-5 text-orange-600" />
              <h2 className="text-lg font-bold text-slate-900">{t('summary.qgs_rework_heading', { count: reworkRows.length })}</h2>
            </div>
            {reworkRows.length === 0 ? (
              <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg p-6 text-center bg-white">
                {t('summary.qgs_no_reworks')}
              </div>
            ) : (
              reworkRows.map(r => <ReworkCard key={`rw-${r.checkId}`} record={r} lookups={lookups} language={language} t={t} />)
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default QgsAuditSummaryPage;
